import { AuthXInterceptor } from '../Config/AuthXInterceptor'

export type StationStaffRole = 'STATION_MANAGER' | 'STATION_ASSOCIATE'
export type StationStaffAccountRole = StationStaffRole | 'EXTERNAL'
export type StationStaffCountry = 'Nigeria' | 'United States' | 'Kenya'

export type ApiEnvelope<T> = {
  type: string
  code: number
  message?: string
  data: T
  meta?: {
    total?: number
    page?: number
    limit?: number
  }
}

export type StationStaffUser = {
  resourceId: string
  firstName: string
  lastName: string
  email: string
  phone?: string
  accountType: string
  createdAt: string
  disabledAt?: string | null
  welcomeEmailSent?: boolean
}

export type StationStaffDirectoryUser = Omit<StationStaffUser, 'accountType'> & {
  accountType: StationStaffRole
}

type UserListEnvelope = {
  data: StationStaffUser[]
  meta?: {
    pagination?: {
      total?: number
      per_page?: number
      current_page?: number
    }
  }
}

export type StationAssignment = {
  resourceId: string
  stationId: string
  role: StationStaffRole
  userResourceId: string
  userFirstName: string
  userLastName: string
  userEmail: string
  createdAt: string
}

export type CreateStationStaffPayload = {
  firstName: string
  lastName: string
  email: string
  country: StationStaffCountry
  phone: string
  role: StationStaffAccountRole
}

export type CreateStationAssignmentPayload = {
  userResourceId: string
  stationId: string
  role: StationStaffRole
}

const normalizeAccountType = (accountType: string) =>
  accountType.trim().toUpperCase().replace(/\s+/g, '_')

const listUsersByRole = (role: StationStaffRole, page: number, limit: number) =>
  AuthXInterceptor.get<UserListEnvelope>('users', {
    params: { page, limit, sortOrder: 'ASC', accountType: role },
  })

const listAllUsersByRole = async (role: StationStaffRole): Promise<StationStaffDirectoryUser[]> => {
  const limit = 100
  const firstResponse = await listUsersByRole(role, 1, limit)
  const firstPage = firstResponse.data?.data ?? []
  const total = firstResponse.data?.meta?.pagination?.total ?? firstPage.length
  const pageSize = firstResponse.data?.meta?.pagination?.per_page ?? limit
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const remainingResponses = await Promise.all(
    Array.from({ length: totalPages - 1 }, (_, index) => listUsersByRole(role, index + 2, limit)),
  )

  return [...firstPage, ...remainingResponses.flatMap((response) => response.data?.data ?? [])]
    .filter((user) => normalizeAccountType(user.accountType) === role)
    .map((user) => ({ ...user, accountType: role }))
}

const listAssignments = (params: {
  page?: number
  limit?: number
  stationId?: string
  userResourceId?: string
}) => AuthXInterceptor.get<ApiEnvelope<StationAssignment[]>>('station-assignments', { params })

const listAllAssignments = async (): Promise<StationAssignment[]> => {
  const limit = 100
  const firstResponse = await listAssignments({ page: 1, limit })
  const firstPage = firstResponse.data?.data ?? []
  const total = firstResponse.data?.meta?.total ?? firstPage.length
  const pageSize = firstResponse.data?.meta?.limit ?? limit
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const remainingResponses = await Promise.all(
    Array.from({ length: totalPages - 1 }, (_, index) =>
      listAssignments({ page: index + 2, limit }),
    ),
  )

  return [...firstPage, ...remainingResponses.flatMap((response) => response.data?.data ?? [])]
}

export const stationStaffService = {
  createUser: (payload: CreateStationStaffPayload) =>
    AuthXInterceptor.post<ApiEnvelope<StationStaffUser>>('staff-users', payload),

  lookupByPhone: (phone: string, country: StationStaffCountry = 'Nigeria') =>
    AuthXInterceptor.get<ApiEnvelope<StationStaffUser>>('staff-users/lookup', {
      params: { phone, country },
    }),

  listAllDirectoryUsers: async () => {
    const [managers, associates] = await Promise.all([
      listAllUsersByRole('STATION_MANAGER'),
      listAllUsersByRole('STATION_ASSOCIATE'),
    ])
    return [...managers, ...associates]
  },

  listAssignments,

  listAllAssignments,

  createAssignment: (payload: CreateStationAssignmentPayload) =>
    AuthXInterceptor.post<ApiEnvelope<StationAssignment>>('station-assignments', payload),

  deleteAssignment: (assignmentResourceId: string) =>
    AuthXInterceptor.delete<ApiEnvelope<{ deleted: true }>>(
      `station-assignments/${encodeURIComponent(assignmentResourceId)}`,
    ),
}
